/**
 * personalisation.js
 *
 * Client-side validation for the personalisation fields rendered by
 * snippets/personalisation-fields.liquid on personalised products.
 *
 * Before the product form is allowed to submit (Add to cart / dynamic
 * checkout), this makes sure that:
 *   - every VISIBLE field marked [data-personalisation-required] has a value
 *     (Name + Message Line 1 for a normal design),
 *   - every VISIBLE design-picker group marked
 *     [data-personalisation-required-group] has a design selected, when that
 *     product offers label designs (unless data-skip-when-owndesign is set
 *     and the customer has actually uploaded a ready-made design - see
 *     below), and
 *   - every VISIBLE "either/or" group marked
 *     [data-personalisation-required-either] has at least one of its
 *     [data-required-path] paths fully filled in. Corporate Branding's
 *     Company Name + Upload your logo (path "standard") and Upload your own
 *     design (path "owndesign") sit side by side, always visible at once
 *     (see snippets/personalisation-fields.liquid) - exactly one of the two
 *     is actually required, whichever the customer fills in, not both. See
 *     validateEitherGroup() below.
 *
 * A required field or group that sits inside a currently-hidden container
 * (for example the standard Name/Message fields while "Branded Logo" is
 * selected, or vice versa) is skipped entirely - it isn't relevant to what
 * the customer is currently doing, so it can't block submission or show an
 * error message for a field they can't even see.
 *
 * On failure the matching [data-error-target] message is revealed (the
 * "hidden" class is removed) and the first invalid field is focused, mirroring
 * the pattern Dawn already uses for its own product-form validation. On
 * success any visible error messages are hidden again.
 *
 * The personalisation box is rendered as its own block, a sibling of the
 * buy-buttons block rather than a descendant of its <form> - so all of its
 * fields carry a form="product-form-<section id>" attribute (see
 * snippets/personalisation-fields.liquid) to associate them with that form
 * for submission, and this script looks the form up the same way (via
 * [data-personalisation-form-id], not root.closest('form'), which would
 * always be null here) so its submit-time validation actually runs.
 *
 * IMPORTANT - listener registration order (fixed 20 Sep):
 * Dawn's own <product-form> custom element (assets/product-form.js) attaches
 * its own 'submit' listener directly on the <form> - via a deferred script,
 * which runs before this file's DOMContentLoaded handler. Per the DOM Event
 * spec, listeners on the SAME element fire in the order they were added,
 * regardless of the {capture: true} flag used below - capture only changes
 * ordering relative to listeners on ANCESTOR elements. That meant Dawn's
 * add-to-cart handler (which calls evt.preventDefault() and immediately
 * fires the AJAX request) was actually running BEFORE this validation could
 * call preventDefault() to block it - so an incomplete personalised product
 * could still be silently added to the cart, with the "please fill this in"
 * message only appearing after the request had already gone out. This
 * produced exactly the "had to click Add to Cart twice" symptom Susie
 * reported. Fixed by attaching the capturing listener on `document` instead
 * of directly on the form - a capture-phase listener on an ANCESTOR node
 * always runs before any listener on the form itself, target included,
 * which is the ordering the {capture: true} flag was originally meant to
 * guarantee.
 */
(function () {
  'use strict';

  function isHiddenField(el) {
    var node = el;
    while (node) {
      if (node.hidden) return true;
      node = node.parentElement;
    }
    return false;
  }

  function getErrorTarget(root, id) {
    if (!id) return null;
    return root.querySelector('#' + CSS.escape(id)) || document.getElementById(id);
  }

  function showError(root, target) {
    var el = getErrorTarget(root, target);
    if (el) el.classList.remove('hidden');
  }

  function hideError(root, target) {
    var el = getErrorTarget(root, target);
    if (el) el.classList.add('hidden');
  }

  function validateField(root, field) {
    var errorTarget = field.getAttribute('data-error-target');
    if (isHiddenField(field)) {
      hideError(root, errorTarget);
      return true;
    }
    var value = (field.value || '').trim();
    if (value === '') {
      showError(root, errorTarget);
      return false;
    }
    hideError(root, errorTarget);
    return true;
  }

  function validateGroup(root, group) {
    var errorTarget = group.getAttribute('data-error-target');
    // A required group that's currently hidden isn't relevant right now,
    // same as a hidden required field above - skip it rather than blocking
    // submission over a choice the customer doesn't need to make.
    if (isHiddenField(group)) {
      hideError(root, errorTarget);
      return true;
    }
    // The design picker (Choose a category / Choose a design) carries this
    // flag - see snippets/personalisation-fields.liquid - because Corporate
    // Branding's "Upload your own design" box sits visible right alongside
    // it rather than behind a toggle: a customer using that box has nothing
    // to pick a swatch colour for, so the design-picker requirement is
    // skipped specifically once their ready-made design is actually
    // uploaded (checked via the either/or group elsewhere on the page),
    // not just because the category itself offers that option.
    if (group.hasAttribute('data-skip-when-owndesign')) {
      var eitherGroup = root.querySelector('[data-personalisation-required-either]');
      if (eitherGroup && pathComplete(eitherGroup, 'owndesign')) {
        hideError(root, errorTarget);
        return true;
      }
    }
    var hasSelection = !!group.querySelector('input[type="radio"]:checked');
    if (!hasSelection) {
      showError(root, errorTarget);
      return false;
    }
    hideError(root, errorTarget);
    return true;
  }

  function fieldHasValue(field) {
    if (field.type === 'file') return !!(field.files && field.files.length);
    return (field.value || '').trim() !== '';
  }

  function pathFields(group, path) {
    return Array.prototype.slice.call(group.querySelectorAll('[data-required-path="' + path + '"]'));
  }

  function pathComplete(group, path) {
    var fields = pathFields(group, path);
    return fields.length > 0 && fields.every(fieldHasValue);
  }

  function pathStarted(group, path) {
    return pathFields(group, path).some(fieldHasValue);
  }

  // See the file header comment - validates an "either/or" group: at least
  // one of its data-required-path paths must be fully filled in, not both.
  // Individual field error messages (data-error-target on each path field,
  // e.g. "please enter your company name") are shown/hidden per field so the
  // customer only ever sees errors for the box they're actually using.
  function validateEitherGroup(root, group) {
    var paths = ['standard', 'owndesign'];

    function clearAllPathErrors() {
      paths.forEach(function (path) {
        pathFields(group, path).forEach(function (f) {
          hideError(root, f.getAttribute('data-error-target'));
        });
      });
    }

    if (isHiddenField(group)) {
      clearAllPathErrors();
      return { valid: true, firstInvalid: null };
    }

    if (pathComplete(group, 'owndesign') || pathComplete(group, 'standard')) {
      clearAllPathErrors();
      return { valid: true, firstInvalid: null };
    }

    // Neither path is complete yet. Flag errors only on whichever one the
    // customer actually appears to be using - a customer filling in "Upload
    // your own design" should never see "please enter your company name",
    // and vice versa - defaulting to the standard fields when neither has
    // been started at all (matches what used to be required by default,
    // before this box could be shown alongside it).
    var flagPath = pathStarted(group, 'owndesign') ? 'owndesign' : 'standard';
    var otherPath = flagPath === 'standard' ? 'owndesign' : 'standard';
    var firstInvalid = null;
    pathFields(group, flagPath).forEach(function (f) {
      if (fieldHasValue(f)) {
        hideError(root, f.getAttribute('data-error-target'));
      } else {
        showError(root, f.getAttribute('data-error-target'));
        if (!firstInvalid) firstInvalid = f;
      }
    });
    pathFields(group, otherPath).forEach(function (f) {
      hideError(root, f.getAttribute('data-error-target'));
    });
    return { valid: false, firstInvalid: firstInvalid };
  }

  function validateRoot(root) {
    var isValid = true;
    var firstInvalid = null;

    var groups = root.querySelectorAll('[data-personalisation-required-group]');
    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      if (!validateGroup(root, group)) {
        isValid = false;
        if (!firstInvalid) firstInvalid = group.querySelector('[data-category-select]') || group;
      }
    }

    var fields = root.querySelectorAll('[data-personalisation-required]');
    for (var j = 0; j < fields.length; j++) {
      var field = fields[j];
      if (!validateField(root, field)) {
        isValid = false;
        if (!firstInvalid) firstInvalid = field;
      }
    }

    var eitherGroups = root.querySelectorAll('[data-personalisation-required-either]');
    for (var k = 0; k < eitherGroups.length; k++) {
      var eitherResult = validateEitherGroup(root, eitherGroups[k]);
      if (!eitherResult.valid) {
        isValid = false;
        if (!firstInvalid) firstInvalid = eitherResult.firstInvalid || eitherGroups[k];
      }
    }

    return { isValid: isValid, firstInvalid: firstInvalid };
  }

  function attachLiveClearing(root) {
    root.addEventListener('input', function (event) {
      var field = event.target.closest('[data-personalisation-required]');
      if (field) validateField(root, field);
      var eitherField = event.target.closest('[data-required-path]');
      if (eitherField) {
        var eitherGroupOnInput = eitherField.closest('[data-personalisation-required-either]');
        if (eitherGroupOnInput) validateEitherGroup(root, eitherGroupOnInput);
      }
    });
    root.addEventListener('change', function (event) {
      var field = event.target.closest('[data-personalisation-required]');
      if (field) validateField(root, field);
      var group = event.target.closest('[data-personalisation-required-group]');
      if (group) validateGroup(root, group);
      var eitherField = event.target.closest('[data-required-path]');
      if (eitherField) {
        var eitherGroupOnChange = eitherField.closest('[data-personalisation-required-either]');
        if (eitherGroupOnChange) validateEitherGroup(root, eitherGroupOnChange);
      }
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-personalisation-fields]');
    if (!roots.length) return;

    roots.forEach(function (root) {
      attachLiveClearing(root);

      var formId = root.getAttribute('data-personalisation-form-id');
      var form = (formId && document.getElementById(formId)) || root.closest('form');
      if (!form) return;

      // Attached on `document`, in the capturing phase, rather than directly
      // on `form` - see the comment at the top of this file for why. A
      // capture-phase listener on an ancestor node is guaranteed to run
      // before any listener on the form itself (Dawn's add-to-cart handler
      // included), so an invalid personalised product is now blocked before
      // product-form.js ever sees the submit event, instead of racing it.
      document.addEventListener(
        'submit',
        function (event) {
          if (event.target !== form) return;
          var result = validateRoot(root);
          if (!result.isValid) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (result.firstInvalid && typeof result.firstInvalid.focus === 'function') {
              result.firstInvalid.focus();
            }
            root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        },
        true
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ===== V178 17.10.26 additions =====
 * 1. ONE-MESSAGE-LINE DESIGNS. Designs named in SINGLE_LINE_DESIGNS only take
 *    Name + Message line 1. Message line 2 is hidden by CSS (rule 14 in
 *    assets/section-related-products.css - add the design name there too) and
 *    any text already typed in it is cleared here when one of these designs is
 *    picked, so it can never print. The label_design metaobject also has a
 *    'single_message_line' field ticked for these designs, as a record.
 * 2. PHOTOGRAPH LABELS PRE-SELECT. When the Photograph Labels category is
 *    opened, its first design (Especially For You) is picked automatically even
 *    if the category holds more than one design, so the upload box appears
 *    straight away, exactly as it always has.
 */
(function () {
  'use strict';
  var SINGLE_LINE_DESIGNS = ['Red Lips I Love You'];

  function clearLine2() {
    document.querySelectorAll('[data-personalisation-fields]').forEach(function (root) {
      var checked = root.querySelector('input[name="properties[Label Design]"]:checked');
      var line2 = root.querySelector('input[name="properties[Message Line 2]"]');
      if (checked && line2 && line2.value && SINGLE_LINE_DESIGNS.indexOf(checked.value) !== -1) {
        line2.value = '';
        line2.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }

  document.addEventListener('change', function (event) {
    if (event.target && event.target.name === 'properties[Label Design]') setTimeout(clearLine2, 0);
  });

  document.addEventListener('click', function (event) {
    var tile = event.target.closest && event.target.closest('[data-category-select="photo-labels"]');
    if (!tile) return;
    setTimeout(function () {
      document.querySelectorAll('[data-design-group="photo-labels"]').forEach(function (group) {
        if (group.hidden) return;
        var radios = group.querySelectorAll('input[type="radio"]');
        if (radios.length && !group.querySelector('input[type="radio"]:checked')) {
          radios[0].checked = true;
          radios[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }, 0);
  });
})();

/* ===== V204 24.10.26 addition =====
 * COLLECTION BUTTON NAMES ON TWO LINES (Susie). On the 'Choose A Collection' buttons, two-word names
 * break onto a second line ('Classic / Designs'); a name with '&' breaks before it ('Love /
 * & Valentines'), except names in AMP_ON_FIRST_LINE, which keep the '&' on the first line
 * ('Wedding & / Engagement', Susie 24 Oct). Names in KEEP_ON_ONE_LINE are not split (Thank You,
 * New Baby; Family & Friends added in V205 24 Oct as a trial - Susie: 'if it doesn't look good we
 * can go back'). The bigger text and deeper buttons are rule 16 in assets/section-related-products.css.
 * Photo Labels shows as 'Photograph / Labels' via that CSS (rule 10 hides its real text), so it
 * is left alone here.
 */
(function () {
  'use strict';
  var KEEP_ON_ONE_LINE = ['Thank You', 'New Baby', 'Family & Friends', 'Photo Labels'];
  var AMP_ON_FIRST_LINE = ['Wedding & Engagement'];

  function splitName(text) {
    var t = text.replace(/\s+/g, ' ').trim();
    if (KEEP_ON_ONE_LINE.indexOf(t) !== -1) return null;
    var i = t.indexOf(' & ');
    if (i > 0) {
      if (AMP_ON_FIRST_LINE.indexOf(t) !== -1) return [t.slice(0, i + 2), t.slice(i + 3)];
      return [t.slice(0, i), t.slice(i + 1)];
    }
    i = t.indexOf(' ');
    if (i > 0) return [t.slice(0, i), t.slice(i + 1)];
    return null;
  }

  function splitTiles() {
    document.querySelectorAll('.personalisation-fields__category-tile').forEach(function (tile) {
      if (tile.getAttribute('data-alx-split')) return;
      tile.setAttribute('data-alx-split', '1');
      var textNodes = Array.prototype.filter.call(tile.childNodes, function (n) {
        return n.nodeType === 3 && n.textContent.trim() !== '';
      });
      if (textNodes.length !== 1) return;
      var parts = splitName(textNodes[0].textContent);
      if (!parts) return;
      var frag = document.createDocumentFragment();
      frag.appendChild(document.createTextNode(parts[0]));
      frag.appendChild(document.createElement('br'));
      frag.appendChild(document.createTextNode(parts[1]));
      tile.replaceChild(frag, textNodes[0]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', splitTiles);
  } else {
    splitTiles();
  }
})();

/* ===== V222 25.10.26 addition =====
 * PROFESSIONAL CORPORATE LABEL DESIGNS (Susie). The 'Professional Designs' collection button
 * (data-category-select="branded-logo-labels") is shown as 'Professional Corporate Label Designs'
 * and made a long full-width button, just above 'Design Your Own Logo Label', so the other
 * buttons sit in tidy rows of three. The category heading inside reads 'Professional Corporate
 * Label Designs Collection'. Theme only - the category metaobject is still named
 * 'Professional Designs'. Runs after the V204 block, so it replaces the split name.
 * V225 (Susie, 25 Oct): all 'Choose A Collection' buttons have rounded corners (12px), set in the same style tag.
 * V226 (Susie, 25 Oct): on corporate-only products (where the personal collections are hidden) the button
 * reads 'Professional / Label Designs' (heading 'Professional Label Designs Collection') and is a normal-sized
 * button, so the six corporate buttons make two rows of three above 'Design Your Own Logo Label'. In that mode
 * the buttons are alphabetical (CORP_ORDER, overriding the older V210 order), Professional last. Re-checked on window load.
 */
(function () {
  'use strict';
  var KEY = 'branded-logo-labels';
  var NEW_NAME = 'Professional Corporate Label Designs';
  var CORP_NAME = 'Professional Label Designs';
  // V226: corporate-only button order - alphabetical, then the two corporate design buttons last.
  var CORP_ORDER = ['awards-promotions', 'congratulations', 'office-celebrations', 'retirement', 'thank-you', KEY, 'branded-logo'];

  function addStyle() {
    if (document.getElementById('alx-v222-style')) return;
    var s = document.createElement('style');
    s.id = 'alx-v222-style';
    s.textContent =
      '.personalisation-fields__category-grid [data-category-select="' + KEY + '"]{grid-column:1 / -1 !important;text-align:center;}' +
      '.personalisation-fields__category-grid [data-category-select="' + KEY + '"] br{display:none;}' +
      '.personalisation-fields__category-grid .personalisation-fields__category-tile{border-radius:12px !important;}' +
      '.personalisation-fields__category-grid.alx-corp-mode [data-category-select="' + KEY + '"]{grid-column:auto !important;}' +
      '.personalisation-fields__category-grid.alx-corp-mode [data-category-select="' + KEY + '"] br{display:inline !important;}' +
      CORP_ORDER.map(function (h, i) { return '.personalisation-fields__category-grid.alx-corp-mode [data-category-select="' + h + '"]{order:' + (i + 1) + ' !important;}'; }).join('');
    document.head.appendChild(s);
  }

  function setTile(tile, parts) {
    Array.prototype.slice.call(tile.childNodes).forEach(function (n) {
      if (n.nodeType === 3 || n.nodeName === 'BR') tile.removeChild(n);
    });
    parts.forEach(function (p, i) {
      if (i) tile.appendChild(document.createElement('br'));
      tile.appendChild(document.createTextNode(p));
    });
  }

  // V226: corporate-only products (personal collections hidden) show 'Professional Label Designs'
  // on a normal-sized button, two lines like the others.
  function isCorporateOnly() {
    var b = document.querySelector('.personalisation-fields__category-grid [data-category-select="birthdays"]');
    return !!b && getComputedStyle(b).display === 'none';
  }

  function rename() {
    var corp = isCorporateOnly();
    document.querySelectorAll('.personalisation-fields__category-grid').forEach(function (grid) {
      grid.classList.toggle('alx-corp-mode', corp);
    });
    document.querySelectorAll('[data-category-select="' + KEY + '"]').forEach(function (tile) {
      setTile(tile, corp ? ['Professional', 'Label Designs'] : [NEW_NAME]);
    });
    document.querySelectorAll('[data-design-group="' + KEY + '"] .personalisation-fields__designs-legend').forEach(function (legend) {
      Array.prototype.forEach.call(legend.childNodes, function (n) {
        if (n.nodeType !== 3) return;
        var t = n.textContent;
        if (corp) {
          if (t.indexOf(CORP_NAME) !== -1) return;
          if (t.indexOf(NEW_NAME) !== -1) n.textContent = t.replace(NEW_NAME, CORP_NAME);
          else if (t.indexOf('Professional Designs') !== -1) n.textContent = t.replace('Professional Designs', CORP_NAME);
        } else {
          if (t.indexOf(NEW_NAME) !== -1) return;
          if (t.indexOf(CORP_NAME) !== -1) n.textContent = t.replace(CORP_NAME, NEW_NAME);
          else if (t.indexOf('Professional Designs') !== -1) n.textContent = t.replace('Professional Designs', NEW_NAME);
        }
      });
    });
  }

  function run() { addStyle(); rename(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', rename);
})();

/* ===== V225 25.10.26 addition =====
 * SECOND 'VIEW ALL DESIGNS LARGER' BUTTON AT THE BOTTOM (Susie). Once a customer presses
 * 'Show N more designs', the list gets long and the 'View all designs larger' button is far
 * back up at the top. So a copy of that button appears where 'Show more' was, under the
 * full list. It opens exactly the same pop-out (it just clicks the top button). Like the top
 * button it only shows on computers and sideways iPads (990px and wider), via the existing
 * .personalisation-fields__view-all styles in snippets/personalisation-fields.liquid.
 */
(function () {
  'use strict';
  document.addEventListener('click', function (event) {
    var more = event.target.closest && event.target.closest('[data-show-more-designs]');
    if (!more) return;
    setTimeout(function () {
      var group = more.closest('[data-design-group]');
      if (!group || group.querySelector('[data-alx-view-all-bottom]')) return;
      var top = group.querySelector('[data-view-all-designs]');
      if (!top) return;
      var copy = document.createElement('button');
      copy.type = 'button';
      copy.className = top.className;
      copy.setAttribute('data-alx-view-all-bottom', '1');
      copy.textContent = top.textContent;
      copy.style.marginTop = '1.6rem';
      copy.addEventListener('click', function () { top.click(); });
      more.parentNode.insertBefore(copy, more.nextSibling);
    }, 0);
  });
})();

/* ===== V227 25.10.26 addition =====
 * 'ONE GIFT, TWO WAYS' LINE (Susie). On personalised products (not corporate-only ones) a short
 * line sits just under 'Choose a design and add your own message' at the top of the label box,
 * telling customers the same gift works for personal or corporate gifting. Hidden on
 * corporate-only products (where the personal collections are hidden - same test as V226).
 */
(function () {
  'use strict';
  var TEXT_BOLD = 'One gift, two ways.';
  var TEXT = ' Perfect for personal and corporate gifting: personalise it with a name and message, or brand it with your company logo. Simply choose the label style that suits your occasion.';

  function addStyle() {
    if (document.getElementById('alx-v227-style')) return;
    var s = document.createElement('style');
    s.id = 'alx-v227-style';
    s.textContent = '.alx-two-ways{margin:16px 0 2px;padding:9px 12px;font-size:1.125rem;line-height:1.45;color:#103948;background:#fffefb;border-left:3px solid #9c7a2e;border-radius:4px;}' +
      '.alx-two-ways strong{font-weight:700;color:#000000;}';
    document.head.appendChild(s);
  }

  function isCorporateOnly() {
    var b = document.querySelector('.personalisation-fields__category-grid [data-category-select="birthdays"]');
    return !!b && getComputedStyle(b).display === 'none';
  }

  function place() {
    addStyle();
    var corp = isCorporateOnly();
    document.querySelectorAll('.personalisation-fields__box > .personalisation-fields__helper-inline').forEach(function (helper) {
      var box = helper.parentNode;
      var existing = box.querySelector(':scope > .alx-two-ways');
      if (corp) { if (existing) existing.remove(); return; }
      if (existing) return;
      var p = document.createElement('p');
      p.className = 'alx-two-ways';
      var b = document.createElement('strong'); b.textContent = TEXT_BOLD;
      p.appendChild(b); p.appendChild(document.createTextNode(TEXT));
      helper.parentNode.insertBefore(p, helper.nextSibling);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', place);
  } else {
    place();
  }
  window.addEventListener('load', place);
})();

/* ===== V228 26.10.26 addition =====
 * ROUNDED PERSONALISE BOX (Susie). The cream 'Personalise Your Bottle' box on product pages gets
 * rounded corners (12px) to match the rounded 'Choose A Collection' buttons inside it (was 2px).
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v228-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v228-style';
  s.textContent = '.personalisation-fields__box{border-radius:12px !important;}';
  (document.head || document.documentElement).appendChild(s);
})();

/* ===== V228 26.10.26 addition (2) =====
 * HIDE 'ONE GIFT, TWO WAYS' FOR BRANDED LABELS (Susie). Once the customer opens 'Design Your Own Logo
 * Label' (branded-logo) or 'Professional Corporate Label Designs' (branded-logo-labels), the
 * 'One gift, two ways' line is hidden - they have already chosen the corporate route. It comes back
 * if they go back to the collections ('Change category') and pick a personal one.
 */
(function () {
  'use strict';
  var BRANDED = ['branded-logo', 'branded-logo-labels'];

  function brandedOpen() {
    return BRANDED.some(function (h) {
      var g = document.querySelector('[data-design-group="' + h + '"]');
      return !!g && !g.closest('[hidden]') && getComputedStyle(g).display !== 'none';
    });
  }

  function update() {
    var hide = brandedOpen();
    document.querySelectorAll('.alx-two-ways').forEach(function (p) {
      p.style.display = hide ? 'none' : '';
    });
  }

  document.addEventListener('click', function () { setTimeout(update, 60); });
  document.addEventListener('change', function () { setTimeout(update, 60); });
  window.addEventListener('load', update);
})();

/* ===== V228 26.10.26 addition (3) =====
 * 1. PROFESSIONAL LABEL DESIGNS HEADING (Susie). Inside the Professional collection the heading
 *    reads 'Professional Label Designs' (was 'Professional Corporate Label Designs Collection').
 *    The collection button itself is unchanged. Runs after the V222 rename (on load as well).
 * 2. GREY BOX AT THE TOP OF THE LABEL BOX (Susie). 'Personalise Your Bottle' and 'Choose a design
 *    and add your own message' sit together in a pale grey box with rounded corners, to match
 *    the Explore Gifts button.
 */
(function () {
  'use strict';
  var KEY = 'branded-logo-labels';
  var HEADING = 'Professional Label Designs';

  function addStyle() {
    if (document.getElementById('alx-v228c-style')) return;
    var s = document.createElement('style');
    s.id = 'alx-v228c-style';
    s.textContent =
      '.personalisation-fields__box > .personalisation-fields__heading{background:#f5f5f3;border-radius:12px 12px 0 0;padding:14px 14px 4px;margin-bottom:0 !important;}' +
      '.personalisation-fields__box > .personalisation-fields__helper-inline{background:#f5f5f3;border-radius:0 0 12px 12px;padding:2px 14px 14px;margin-top:0 !important;text-align:center;}';
    document.head.appendChild(s);
  }

  function renameHeading() {
    document.querySelectorAll('[data-design-group="' + KEY + '"] .personalisation-fields__designs-legend').forEach(function (legend) {
      Array.prototype.forEach.call(legend.childNodes, function (n) {
        if (n.nodeType === 3 && /Professional/.test(n.textContent) && n.textContent.trim() !== HEADING) {
          n.textContent = n.textContent.replace(/Professional[^]*?(Collection|Designs)\s*$/, HEADING).replace(/Professional.*$/, HEADING);
        }
      });
    });
  }

  function run() { addStyle(); renameHeading(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run);
})();

/* ===== V228 26.10.26 addition (4) =====
 * GREY BOX FULL WIDTH + BIGGER TITLE (Susie). The grey box at the top of the label box now runs the
 * full width of the cream box (edge to edge and flush with its top, like a header strip), and the
 * 'Personalise Your Bottle' title is 25% bigger. The line under it stays the same size.
 * Also (trial): the label box loses its cream background (white instead); its border stays.
 */
(function () {
  'use strict';
  // Susie 26 Oct: try the whole label box without its cream background (border kept).
  if (!document.getElementById('alx-v228d-style')) {
    var st = document.createElement('style');
    st.id = 'alx-v228d-style';
    st.textContent = '.personalisation-fields__box{background:#ffffff !important;}';
    (document.head || document.documentElement).appendChild(st);
  }
  function widen() {
    document.querySelectorAll('.personalisation-fields__box').forEach(function (box) {
      var cs = getComputedStyle(box);
      var pl = parseFloat(cs.paddingLeft) || 0;
      var pr = parseFloat(cs.paddingRight) || 0;
      box.querySelectorAll(':scope > .personalisation-fields__heading, :scope > .personalisation-fields__helper-inline').forEach(function (el) {
        el.style.setProperty('margin-left', (-pl) + 'px', 'important');
        el.style.setProperty('margin-right', (-pr) + 'px', 'important');
      });
      var h = box.querySelector(':scope > .personalisation-fields__heading');
      var helper = box.querySelector(':scope > .personalisation-fields__helper-inline');
      // Header strip: flush with the top of the cream box too, top corners matching its 12px corners.
      if (h) {
        h.style.setProperty('margin-top', (-(parseFloat(cs.paddingTop) || 0)) + 'px', 'important');
        h.style.setProperty('border-radius', '12px 12px 0 0', 'important');
      }
      if (helper) helper.style.setProperty('border-radius', '0', 'important');
      if (h && !h.getAttribute('data-alx-big')) {
        h.setAttribute('data-alx-big', '1');
        var fs = parseFloat(getComputedStyle(h).fontSize) || 0;
        if (fs) h.style.setProperty('font-size', (fs * 1.25) + 'px', 'important');
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', widen);
  } else {
    widen();
  }
  window.addEventListener('load', widen);
  window.addEventListener('resize', function () {
    document.querySelectorAll('.personalisation-fields__box > .personalisation-fields__heading').forEach(function (h) {
      h.removeAttribute('data-alx-big'); h.style.removeProperty('font-size');
    });
    widen();
  });
})();

/* ===== V230 26.10.26 addition =====
 * 1. LIVE LABEL PREVIEW HEADING (Susie). The 'Live label preview' heading above the preview is 22/18
 *    of its old size (1.4rem -> 1.4rem x 22/18, about 15.4px -> 18.8px at this theme's 11px rem) and
 *    dark gold #9c7a2e, the gold used on the 'One gift, two ways' border. Its old size/colour are in
 *    snippets/personalisation-fields.liquid and rule 1 of assets/section-related-products.css.
 * 2. 'ONE GIFT, TWO WAYS' NEARER THE COLLECTION BUTTONS (Susie). The big gap under the box came from
 *    the 'Choose A Collection' heading's 8.192rem (about 90px) top margin plus 12px top padding. When
 *    the box is there, that margin is removed and the padding halved, so the box sits just above
 *    'Choose A Collection' and the buttons. The box's own 16px top margin is unchanged; products
 *    without the box (corporate-only) keep the old spacing.
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v230-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v230-style';
  s.textContent =
    '.personalisation-fields .personalisation-fields__preview-label{font-size:calc(1.4rem * 22 / 18) !important;color:#9c7a2e !important;}' +
    '.personalisation-fields__box > .alx-two-ways + .personalisation-fields__design-picker .personalisation-fields__category-picker-legend{margin-top:0 !important;padding-top:6px !important;}';
  (document.head || document.documentElement).appendChild(s);
})();

/* ===== V231 26.10.26 addition =====
 * PALER PRINT NOTE (Susie). The 'We'll print your label exactly as it appears in your preview...' note
 * under the message lines (normal labels; text and left border are rule 6 in
 * assets/section-related-products.css) gets the same very pale cream as the 'One gift, two ways' box:
 * #faf5ee -> #fffefb. The old #faf5ee is set by the V185 rule in snippets/personalisation-fields.liquid,
 * which stays as it is for the Photograph Labels note and the other cream notes.
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v231-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v231-style';
  s.textContent = '[data-fields-standard]:has(> [data-photo-upload-field][hidden])::after{background:#fffefb !important;}';
  (document.head || document.documentElement).appendChild(s);
})();

/* ===== V231 26.10.26 addition (2) =====
 * 'CHOOSE A COLLECTION' STYLED LIKE 'LIVE LABEL PREVIEW' (Susie). Uppercase, dark gold #9c7a2e,
 * calc(1.4rem * 22 / 18) and 0.05em letter-spacing, the same as the V230 'Live label preview' heading
 * (both headings use the normal font weight, so that is unchanged). Was black, 1.4rem, not uppercase,
 * no letter-spacing (snippets/personalisation-fields.liquid, rules 1 and 8 of
 * assets/section-related-products.css). The gap between it and the first collection buttons is about
 * halved (about 17px -> 8.5px): heading bottom padding 0.3rem -> 0 and the white button area's top
 * padding 14px -> 8.5px. Applies on every product, with or without the 'One gift, two ways' box.
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v231b-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v231b-style';
  s.textContent =
    '.personalisation-fields .personalisation-fields__category-picker-legend{text-transform:uppercase !important;color:#9c7a2e !important;font-size:calc(1.4rem * 22 / 18) !important;letter-spacing:0.05em !important;padding-bottom:0 !important;}' +
    '.personalisation-fields__category-picker .personalisation-fields__category-grid{padding-top:8.5px !important;}';
  (document.head || document.documentElement).appendChild(s);
})();

/* ===== V231 26.10.26 addition (3) =====
 * 'VIEW LARGER PREVIEW' AS A BUTTON (Susie). The underlined link under the live label preview
 * (button.personalisation-fields__enlarge, snippets/personalisation-fields.liquid) becomes a small
 * box: very pale cream #fffefb, 1px dark gold #9c7a2e border, 12px corners, 8px 18px padding, no
 * underline. Text colour and size are unchanged. It hugs its text (fit-content) and stays centred
 * with auto side margins; it was a full-width block with centred text. The lightbox it opens is not
 * touched.
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v231c-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v231c-style';
  s.textContent =
    '.personalisation-fields__preview > .personalisation-fields__enlarge{display:block;width:-moz-fit-content;width:fit-content;margin-top:calc(0.24rem + 14px);margin-bottom:14px;margin-left:auto;margin-right:auto;padding:11px 46px;background:#fffefb;color:#9c7a2e;border:1px solid #9c7a2e;border-radius:12px;text-decoration:none;text-transform:uppercase;letter-spacing:0.05em;font-size:calc(1.4rem * 22 / 18);}';
  (document.head || document.documentElement).appendChild(s);
})();

/* ===== V232 26.10.26 addition =====
 * 'VIEW LARGER PREVIEW' POP-UP FIXES (Susie's screenshot, 1920px desktop). The pop-up itself is built
 * by assets/personalisation-preview.js (V81); its base styles are in snippets/personalisation-fields.liquid.
 * 1. Close X always on screen: the round close button now sits fixed 16px from the top-right corner of
 *    the window (it was 1.4rem above the panel's corner, so it went off-screen when the panel was tall),
 *    44px, white with a navy X, a thin border and a soft shadow. Esc and a click on the dark backdrop
 *    already closed it (V81) and still do.
 * 2. Panel fits the window: its width is also capped at 90vh x 3/2, so the 3:2 label is never more than
 *    90% of the window height and stays centred, instead of running past the top on short screens.
 * 3. Nothing to show yet: if no label design is chosen (and no ready-made design uploaded), the button
 *    no longer opens a blank white pop-up; a short note appears under it for a few seconds instead.
 */
(function () {
  'use strict';
  if (!document.getElementById('alx-v232-style')) {
    var s = document.createElement('style');
    s.id = 'alx-v232-style';
    s.textContent =
      '.personalisation-fields__lightbox .personalisation-fields__lightbox-inner{width:min(94vw, 110rem, calc(90vh * 3 / 2)) !important;max-height:90vh;}' +
      '.personalisation-fields__lightbox .personalisation-fields__lightbox-canvas{max-height:90vh;}' +
      '.personalisation-fields__lightbox .personalisation-fields__lightbox-close{position:fixed !important;top:16px !important;right:16px !important;z-index:1001;width:44px !important;height:44px !important;display:flex;align-items:center;justify-content:center;padding:0;background:#ffffff !important;color:#103948 !important;border:1px solid rgba(16, 57, 72, 0.3) !important;border-radius:50%;box-shadow:0 2px 10px rgba(0, 0, 0, 0.35) !important;font-size:30px !important;line-height:1 !important;}' +
      '.alx-enlarge-note{margin:8px auto 0;max-width:30rem;text-align:center;font-size:1.3rem;line-height:1.4;color:#103948;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function hasSomethingToShow(root) {
    if (!root) return true;
    if (root.querySelector('input[name="properties[Label Design]"]:checked')) return true;
    var own = root.querySelector('input[type="file"][data-required-path="owndesign"]');
    return !!(own && own.files && own.files.length);
  }

  // Capture phase on document, so this runs before the button's own click handler
  // in personalisation-preview.js and can stop it opening an empty pop-up.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-personalisation-enlarge]');
    if (!btn) return;
    if (hasSomethingToShow(btn.closest('[data-personalisation-fields]'))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var note = btn.parentNode.querySelector(':scope > .alx-enlarge-note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'alx-enlarge-note';
      note.setAttribute('role', 'status');
      note.textContent = 'Choose a label design first to see a larger preview';
      btn.parentNode.insertBefore(note, btn.nextSibling);
    }
    note.hidden = false;
    clearTimeout(note._alxTimer);
    note._alxTimer = setTimeout(function () { note.hidden = true; }, 4000);
  }, true);
})();

/* ===== V233 26.10.26 addition =====
 * BIGGER FIELD LABELS (Susie). The floating labels inside the personalisation text boxes ('Name',
 * 'Message - line 1', 'Message - line 2 (optional)', and the Corporate Branding 'Name, Tagline or Logo
 * Message' / 'Message (optional)') are 20% bigger: 1.9rem -> 2.28rem at rest, 1.2rem -> 1.44rem when
 * raised (snippets/personalisation-fields.liquid). The resting label is moved up 0.285rem so it stays
 * centred in the box as before; the raised label sits 0.14rem higher (0.5rem -> 0.36rem) and the typed
 * text 0.2rem lower (padding 2.2rem/0.8rem -> 2.4rem/0.6rem, same box height), so the bigger raised
 * label keeps clear of what the customer types. The typed text size (1.9rem) is unchanged.
 * (The 'View larger preview' spacing change of the same version is in the V231 (3) rule above:
 * top margin calc(0.24rem + 30px) -> calc(0.24rem + 14px), new 14px bottom margin.)
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v233-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v233-style';
  s.textContent =
    '.personalisation-fields .field .field__label{font-size:2.28rem !important;top:calc(var(--inputs-border-width) + 0.715rem) !important;}' +
    '.personalisation-fields .field .field__input:focus ~ .field__label,.personalisation-fields .field .field__input:not(:placeholder-shown) ~ .field__label,.personalisation-fields .field .field__input:-webkit-autofill ~ .field__label{font-size:1.44rem !important;top:calc(var(--inputs-border-width) + 0.36rem) !important;}' +
    '.personalisation-fields .field .field__input:focus,.personalisation-fields .field .field__input:not(:placeholder-shown),.personalisation-fields .field .field__input:-webkit-autofill{padding-top:2.4rem !important;padding-bottom:0.6rem !important;}';
  (document.head || document.documentElement).appendChild(s);
})();

/* ===== V233 26.10.26 addition (2) =====
 * 1. 'VIEW ALL DESIGNS LARGER' LIKE 'VIEW LARGER PREVIEW' (Susie). The swirl ornaments either side
 *    (::before/::after in snippets/personalisation-fields.liquid) are removed, and the button gets the
 *    same look as the 'View larger preview' button: #fffefb, 1px #9c7a2e border, 12px corners, #9c7a2e
 *    text, uppercase, 0.05em letter-spacing, calc(1.4rem * 22 / 18), 11px 46px padding, normal weight.
 *    It hugs its text and stays centred (was full width). Same class on every collection's button and
 *    on the V225 copy under 'Show more'. It stays hidden below 990px as before (display not touched).
 * 2. 'SHOW N MORE DESIGNS' TEXT IN DARK GOLD (Susie): #103948 -> #9c7a2e. Box, border and size unchanged.
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v233b-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v233b-style';
  s.textContent =
    '.personalisation-fields .personalisation-fields__view-all::before,.personalisation-fields .personalisation-fields__view-all::after{content:none !important;display:none !important;}' +
    '.personalisation-fields .personalisation-fields__view-all{width:-moz-fit-content !important;width:fit-content !important;max-width:100%;margin-left:auto !important;margin-right:auto !important;justify-content:center !important;padding:11px 46px !important;background:#fffefb !important;border:1px solid #9c7a2e !important;border-radius:12px !important;color:#9c7a2e !important;text-transform:uppercase !important;letter-spacing:0.05em !important;font-size:calc(1.4rem * 22 / 18) !important;font-weight:400 !important;}' +
    '.personalisation-fields .personalisation-fields__show-more{color:#9c7a2e !important;}';
  (document.head || document.documentElement).appendChild(s);
})();

/* ===== V234 26.10.26 addition =====
 * 'VIEW ALL DESIGNS LARGER' EVENLY SPACED (Susie). The top button (the one with data-view-all-designs,
 * above the labels in each collection; not the V225 copy under 'Show more') sat about 10px under the
 * collection heading (heading bottom padding 0.15rem, button top margin 0, plus the line's own spacing)
 * but about 33px above the first row of labels (button bottom margin 1rem = 11px + the white label
 * area's 22px top padding, rule 7 of assets/section-related-products.css). Now about 19px each side:
 * button top margin 0 -> 9px, bottom margin unchanged (1rem), and the label area's top padding
 * 22px -> 8px only where it comes straight after this button. Computers and sideways iPads only
 * (990px and wider), where the button shows; phones are unchanged.
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v234-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v234-style';
  s.textContent =
    '@media screen and (min-width: 990px){' +
    '.personalisation-fields .personalisation-fields__view-all[data-view-all-designs]{margin-top:9px !important;}' +
    '.personalisation-fields [data-view-all-designs] + .personalisation-fields__design-grid{padding-top:8px !important;}' +
    '}';
  (document.head || document.documentElement).appendChild(s);
})();

/* ===== V234 26.10.26 addition (2) =====
 * 'SHOW N MORE DESIGNS' LIKE 'VIEW LARGER PREVIEW' (Susie). The full-width bar under the labels in every
 * collection (button.personalisation-fields__show-more) gets the same look as the 'View larger preview'
 * button: #fffefb, 1px #9c7a2e border, 12px corners, #9c7a2e text, uppercase, 0.05em letter-spacing,
 * calc(1.4rem * 22 / 18), normal weight (was 600), 11px 46px padding, no underline. It hugs its text and
 * is centred (auto side margins); its 1rem top margin is kept. Display is not touched, so it still
 * disappears once pressed (there is no 'Show fewer').
 */
(function () {
  'use strict';
  if (document.getElementById('alx-v234b-style')) return;
  var s = document.createElement('style');
  s.id = 'alx-v234b-style';
  s.textContent =
    '.personalisation-fields .personalisation-fields__show-more{width:-moz-fit-content !important;width:fit-content !important;max-width:100%;margin-left:auto !important;margin-right:auto !important;padding:11px 46px !important;background:#fffefb !important;border:1px solid #9c7a2e !important;border-radius:12px !important;color:#9c7a2e !important;text-transform:uppercase !important;letter-spacing:0.05em !important;font-size:calc(1.4rem * 22 / 18) !important;font-weight:400 !important;text-decoration:none !important;}';
  (document.head || document.documentElement).appendChild(s);
})();
